// Start of the file - Add these imports at the top
import React, { useState, useEffect } from "react";
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

const ProductCard = ({ product, onAddToCart, onBuyNow }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Convert single image to array if necessary
  const imageArray = product.images && product.images.length > 0 
    ? product.images 
    : (product.image ? [product.image] : []);
  const hasMultipleImages = imageArray.length > 1;
  const hasImages = imageArray.length > 0;

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle swipe gestures for mobile
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    if (!hasMultipleImages) return;
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    if (!hasMultipleImages) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!hasMultipleImages || !touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentImageIndex < imageArray.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
    if (isRightSwipe && currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
    
    // Reset touch values
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Handle arrow clicks for desktop navigation
  const handleNextImage = (e) => {
    e.stopPropagation();
    if (currentImageIndex < imageArray.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };
  
  const handlePrevImage = (e) => {
    e.stopPropagation();
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  // Desktop Layout - Horizontal card with proper height
  if (!isMobile) {
    return (
      <div 
        className="group relative bg-white rounded-2xl overflow-hidden shadow-lg transition-all duration-500 hover:shadow-2xl flex flex-row min-h-[320px]"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Product Image Container - Fixed width */}
        <div 
          className="relative w-72 h-full flex-shrink-0 overflow-hidden bg-gray-100"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {!imageLoaded && hasImages && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
          )}
          
          {hasImages ? (
            <div className="relative w-full h-full">
              {/* Images container with proper positioning */}
              <div className="relative w-full h-full">
                {imageArray.map((img, index) => (
                  <div 
                    key={index}
                    className={`absolute inset-0 w-full h-full transition-opacity duration-300 ${
                      index === currentImageIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                    }`}
                  >
                    {/* Here's the updated LazyLoadImage with proper properties */}
                    <LazyLoadImage
                      src={img}
                      alt={`${product.title} - View ${index + 1}`}
                      effect="blur"
                      wrapperClassName="block w-full h-full"
                      className={`w-full h-full object-cover transition-transform duration-300 ${
                        isHovered ? 'scale-110' : 'scale-100'
                      }`}
                      afterLoad={() => index === 0 && setImageLoaded(true)}
                      threshold={200}
                      // Important - these guarantee the image displays properly
                      width="100%"
                      height="100%"
                      visibleByDefault={index === 0}
                      placeholderSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 300'%3E%3Crect width='300' height='300' fill='%23f3f4f6'/%3E%3C/svg%3E"
                    />
                  </div>
                ))}
              </div>
              
              {/* Navigation arrows - only show if multiple images */}
              {hasMultipleImages && (
                <div className="absolute inset-0 z-20">
                  <button
                    onClick={handlePrevImage}
                    className={`absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition-opacity ${
                      currentImageIndex === 0 ? 'opacity-50 cursor-not-allowed' : 'opacity-100'
                    }`}
                    disabled={currentImageIndex === 0}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  <button
                    onClick={handleNextImage}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition-opacity ${
                      currentImageIndex === imageArray.length - 1 ? 'opacity-50 cursor-not-allowed' : 'opacity-100'
                    }`}
                    disabled={currentImageIndex === imageArray.length - 1}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  
                  {/* Dots indicator */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                    {imageArray.map((_, index) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex(index);
                        }}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          index === currentImageIndex ? 'bg-white w-4' : 'bg-white bg-opacity-50'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <span className="text-gray-400">No image available</span>
            </div>
          )}
          
          {/* Gradient Overlay */}
          <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent transition-opacity duration-300 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}></div>
        </div>

        {/* Product Info - Takes remaining space with proper padding */}
        <div className="flex-1 p-6 flex flex-col justify-between min-w-0">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-1 hover:text-red-600 transition-colors">
              {product.title}
            </h3>
            
            {/* Price */}
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl font-bold text-red-600">{product.price}</span>
              {product.originalPrice && (
                <span className="text-sm text-gray-500 line-through">{product.originalPrice}</span>
              )}
            </div>

            {/* Description - Limited to 2 lines */}
            {product.description && (
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {product.description}
              </p>
            )}

            {/* Features - Show only 2 on desktop */}
            {product.features && product.features.length > 0 && (
              <ul className="text-sm text-gray-600 mb-6 space-y-1">
                {product.features.slice(0, 2).map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="line-clamp-1">{feature}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Action Buttons - Full width with proper spacing */}
          <div className="flex gap-3 mt-auto">
            <button
              onClick={() => onAddToCart(product)}
              disabled={product.inStock === false}
              className={`flex-1 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2
                ${product.inStock === false 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-gray-900 text-white hover:bg-gray-800 active:scale-95'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              Add to Cart
            </button>
            <button
              onClick={() => onBuyNow(product)}
              disabled={product.inStock === false}
              className={`flex-1 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2
                ${product.inStock === false 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-red-600 text-white hover:bg-red-700 active:scale-95'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Buy Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Mobile Layout - Compact vertical card, minimal info
  return (
    <div className="group relative bg-white rounded-xl overflow-hidden shadow transition-all duration-300 active:scale-98">
      {/* Mobile Image Container - Square aspect ratio */}
      <div 
        className="relative aspect-square overflow-hidden bg-gray-100"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {!imageLoaded && hasImages && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
        )}
        
        {hasImages ? (
          <div className="relative w-full h-full">
            {/* Images */}
            <div className="relative w-full h-full">
              {imageArray.map((img, index) => (
                <div 
                  key={index}
                  className={`absolute inset-0 w-full h-full transition-opacity duration-300 ${
                    index === currentImageIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                  }`}
                >
                  {/* Mobile LazyLoadImage with proper properties */}
                  <LazyLoadImage
                    src={img}
                    alt={`${product.title} - View ${index + 1}`}
                    effect="blur"
                    wrapperClassName="block w-full h-full"
                    className="w-full h-full object-cover"
                    afterLoad={() => index === 0 && setImageLoaded(true)}
                    threshold={200}
                    width="100%"
                    height="100%"
                    visibleByDefault={index === 0}
                    placeholderSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 300'%3E%3Crect width='300' height='300' fill='%23f3f4f6'/%3E%3C/svg%3E"
                  />
                </div>
              ))}
            </div>
            
            {/* Dots indicator - only show if multiple images */}
            {hasMultipleImages && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1 z-20">
                {imageArray.map((_, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(index);
                    }}
                    className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                      index === currentImageIndex ? 'bg-white w-3' : 'bg-white bg-opacity-50'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-200">
            <span className="text-gray-400 text-sm">No image</span>
          </div>
        )}
        
        
        {/* Out of Stock Overlay */}
        {product.inStock === false && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30">
            <span className="bg-red-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
              Out of Stock
            </span>
          </div>
        )}
      </div>

      {/* Mobile Product Info - Minimal */}
      <div className="p-3">
        <h3 className="text-sm font-bold text-gray-900 mb-1 line-clamp-1">
          {product.title}
        </h3>
        
        <div className="flex items-center gap-2 mb-2">
          <span className="text-base font-bold text-red-600">{product.price}</span>
          {product.originalPrice && (
            <span className="text-xs text-gray-500 line-through">{product.originalPrice}</span>
          )}
        </div>

        {/* Mobile Action Buttons - Compact */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => onAddToCart(product)}
            disabled={product.inStock === false}
            className={`py-2 rounded-lg text-xs font-semibold transition-all duration-300 flex items-center justify-center gap-1
              ${product.inStock === false 
                ? 'bg-gray-200 text-gray-400' 
                : 'bg-gray-900 text-white active:scale-95'}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <span className="hidden xs:inline">Add</span>
          </button>
          <button
            onClick={() => onBuyNow(product)}
            disabled={product.inStock === false}
            className={`py-2 rounded-lg text-xs font-semibold transition-all duration-300 flex items-center justify-center gap-1
              ${product.inStock === false 
                ? 'bg-gray-200 text-gray-400' 
                : 'bg-red-600 text-white active:scale-95'}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span className="hidden xs:inline">Buy</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;